import {
  getStoredSession,
  removeStoredSession,
  setStoredSession,
} from "@/shared/auth/fetch/authStorage";
import {
  login as apiLogin,
  logout as apiLogout,
} from "@/shared/auth/fetch/sessionApi";
import type { LoginCredentials, Session } from "@/shared/auth/model/types";
import { useCallback, useMemo, useState } from "react";

type AuthentecatedSession = Session & { isAuthenticated: true };
type UnauthenticatedSession = { isAuthenticated: false };
export const useAuth = () => {
  const [session, setSession] = useState<Session | null>(getStoredSession());

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      const tokens = await apiLogin(credentials);
      setStoredSession(tokens);
      setSession(tokens);
      return tokens;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error("Logout API call failed:", error);
      // APIエラーでもローカルセッションはクリアする
    } finally {
      // API呼び出しが失敗してもローカルセッションは必ずクリア
      removeStoredSession();
      setSession(null);
    }
  }, []);

  const updateAuthState = useCallback((session: Session | null) => {
    if (session) {
      setStoredSession(session);
      setSession(session);
    } else {
      removeStoredSession();
      setSession(null);
    }
  }, []);

  const authentecatedSession: AuthentecatedSession | UnauthenticatedSession =
    useMemo(
      () =>
        session
          ? {
              isAuthenticated: true,
              ...session,
            }
          : { isAuthenticated: false },
      [session],
    );

  const { isXmileSystemAdmin, isSystemAdmin } = useMemo(
    () => ({
      isSystemAdmin: !!session && session.user.role === "system_admin",
      isXmileSystemAdmin:
        !!session &&
        session.user.role === "system_admin" &&
        session.user.companyId === 1,
    }),
    [session],
  );

  return {
    session: authentecatedSession,
    login,
    logout,
    updateAuthState,
    isXmileSystemAdmin,
    isSystemAdmin,
  };
};
